import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchAddressSuggestions, type IDadataSuggestion } from 'shared/services/dadataSuggest';
import { useApplicationContext } from 'shared/utils/applicationContext';

interface IUseAddressSuggestResult {
  inputValue: string;
  suggestions: IDadataSuggestion[];
  open: boolean;
  onChange: (value: string) => void;
  onSelect: (suggestion: IDadataSuggestion) => void;
  onClose: () => void;
}

const DEBOUNCE_MS = 300;

export function useAddressSuggest(): IUseAddressSuggestResult {
  const context = useApplicationContext();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<IDadataSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const requestIdRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cancelPendingSearch = useCallback(() => {
    requestIdRef.current += 1;
    clearTimer();
  }, [clearTimer]);

  useEffect(() => {
    return cancelPendingSearch;
  }, [cancelPendingSearch]);

  const onChange = useCallback(
    (value: string) => {
      const requestId = requestIdRef.current + 1;

      requestIdRef.current = requestId;
      setInputValue(value);
      clearTimer();

      if (!value.trim()) {
        setSuggestions([]);
        setOpen(false);

        return;
      }

      timerRef.current = setTimeout(async () => {
        const result = await fetchAddressSuggestions(context, { query: value }).catch(() => []);

        if (requestIdRef.current !== requestId) {
          return;
        }

        setSuggestions(result);
        setOpen(result.length > 0);
      }, DEBOUNCE_MS);
    },
    [clearTimer, context],
  );

  const onSelect = useCallback(
    (suggestion: IDadataSuggestion) => {
      cancelPendingSearch();
      setInputValue(suggestion.unrestricted_value);
      setSuggestions([]);
      setOpen(false);
    },
    [cancelPendingSearch],
  );

  const onClose = useCallback(() => {
    cancelPendingSearch();
    setOpen(false);
  }, [cancelPendingSearch]);

  return { inputValue, suggestions, open, onChange, onSelect, onClose };
}
